import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  profile_picture?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  lastActivity: number;
  timeUntilLogout: number;
  extendSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Session timeout DISABLED - users stay logged in for 1 year (matching JWT expiry)
const SESSION_TIMEOUT = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
const WARNING_TIME = 0; // No warning - disabled
const REFRESH_INTERVAL = 0; // No refresh - disabled

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [timeUntilLogout, setTimeUntilLogout] = useState(SESSION_TIMEOUT);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTokenRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const handleLogout = useCallback(async () => {
    clearAllTimeouts();
    
    // Call logout API
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {});
    } catch (error) {
      console.log('Logout API call failed:', error);
    }
    
    // Clear client state
    setUser(null);
    localStorage.removeItem('token');
    lastActivityRef.current = 0;
    setLastActivity(0);
    setTimeUntilLogout(SESSION_TIMEOUT);
    
    // Force redirect to login page
    window.location.href = '/login';
  }, []);

  useEffect(() => {
    // Configure axios for cookie-based auth
    // Set up axios to use Bearer token
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    // Check authentication status on app load
    checkAuthStatus();

    // Add axios interceptor to handle errors
    // IMPORTANT: Do NOT auto-logout on errors - let user stay logged in
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        // Just log the error, don't auto-logout
        // User will only be logged out when they manually logout
        // or when they try to login and fail
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.log('Auth error:', error.response?.status, '- NOT auto-logging out');
        }
        return Promise.reject(error);
      }
    );

    // Add request interceptor to include Bearer token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
      axios.interceptors.request.eject(requestInterceptor);
    };
  }, [handleLogout]);


  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    
    try {
      // Set token in axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await axios.get(`${API_BASE_URL}/auth/me`);
      setUser(response.data.user);
      startActivityMonitoring();
    } catch (error: any) {
      // Only log if it's not a 403 (expected when not logged in)
      if (error.response?.status !== 403) {
        console.error('Auth check failed:', error);
      }
      // Only remove token on actual auth errors (401/403), not network errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        setUser(null);
        localStorage.removeItem('token');
      } else {
        // Network error or server error - keep token, user might still be valid
        console.log('Auth check failed due to network/server error, keeping token');
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const clearAllTimeouts = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (refreshTokenRef.current) {
      clearTimeout(refreshTokenRef.current);
      refreshTokenRef.current = null;
    }
  };

  const showLogoutWarning = () => {
    const confirmed = window.confirm(
      'Your session will expire in 5 minutes due to inactivity. Click OK to extend your session or Cancel to logout now.'
    );
    
    if (confirmed) {
      extendSession();
    } else {
      handleLogout();
    }
  };

  const resetActivityTimer = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
    setTimeUntilLogout(SESSION_TIMEOUT);

    clearAllTimeouts();

    // DISABLED: Auto-logout functionality removed - no timeouts set
    // Users will stay logged in indefinitely
  }, [handleLogout]);

  const extendSession = useCallback(() => {
    resetActivityTimer();
  }, [resetActivityTimer]);


  const startActivityMonitoring = useCallback(() => {
    if (!user) return;

    // Reset activity timer initially
    const now = Date.now();
    lastActivityRef.current = now;
    setLastActivity(now);
    setTimeUntilLogout(SESSION_TIMEOUT);

    clearAllTimeouts();

    // DISABLED: Auto-logout functionality removed - users will stay logged in
    // No timeout warnings
    // No automatic logout
    // No countdown timer

    // Activity event listeners - kept for potential future use
    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      // Activity tracking - but NO auto-logout
      const now = Date.now();
      lastActivityRef.current = now;
      setLastActivity(now);
      setTimeUntilLogout(SESSION_TIMEOUT);

      // Clear any existing timeouts
      clearAllTimeouts();

      // AUTO-LOGOUT COMPLETELY DISABLED - no timeouts set
      // Users stay logged in until they manually log out or token expires (1 year)
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Cleanup function
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
    };
  }, [user, handleLogout]);

  const login = async (username: string, password: string) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        username,
        password,
      });

      const { user, token } = response.data;
      
      // Store token in localStorage
      localStorage.setItem('token', token);

      // Set token in axios defaults
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(user);
      startActivityMonitoring();

      // Dispatch custom event to notify other contexts (like LeadsContext) that login happened
      window.dispatchEvent(new Event('auth-login'));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const logout = () => {
    handleLogout();
  };

  // Cleanup effect
  useEffect(() => {
    return () => {
      clearAllTimeouts();
    };
  }, []);

  // Start monitoring when user logs in
  useEffect(() => {
    if (user) {
      startActivityMonitoring();
    } else {
      clearAllTimeouts();
    }
  }, [user]); // Remove startActivityMonitoring from dependencies

  const value = {
    user,
    isAuthenticated: !!user,
    loading,
    login,
    logout,
    lastActivity,
    timeUntilLogout,
    extendSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}